import React from 'react';
import { removeParametersFromUrl } from '@wix/marketplace-component';
import { getPropsByRoute } from './seo-page.utils';
import type { IRoute } from '@wix/app-market-core-types';
import { RoutePath } from '@wix/app-market-core-types';
import { AppPageSEOPage } from './app-page-seo-page';
import { SEOPage } from './seo-page';
import type { TFunction } from 'react-i18next';

export interface SEOContainerProps {
  newRoute: IRoute;
  metaTagsAggregator: string[];
  getHrefByRouteAndLanguage: (r: IRoute, languageCode: string) => string;
  t: TFunction;
}

export function SEOContainer({
  newRoute,
  metaTagsAggregator,
  getHrefByRouteAndLanguage,
  t,
}: SEOContainerProps) {
  // hreflang alternates must match the canonical exactly: no query params
  // (tracking params from the entry URL, the subCat param Route adds to every
  // sub-category href) and no homepage trailing slash. Reuse the helper
  // MarketplaceRouterSeo uses for rel="canonical" so the two never drift.
  // Search results keep only their ?query= term, since it defines the page.
  const getHreflangHref = (route: IRoute, languageCode: string): string => {
    const href = getHrefByRouteAndLanguage(route, languageCode);
    const cleanHref = removeParametersFromUrl(href) ?? '';
    if (route.path === RoutePath.SEARCH_RESULTS) {
      const queryParam = href
        .split('?')[1]
        ?.split('&')
        .find((param) => param.startsWith('query='));
      return queryParam ? `${cleanHref}?${queryParam}` : cleanHref;
    }
    return cleanHref;
  };

  if (newRoute.path === RoutePath.APP_PAGE) {
    return (
      <AppPageSEOPage
        metaTagsAggregator={metaTagsAggregator}
        getHref={(languageCode: string) => {
          return getHreflangHref(newRoute, languageCode);
        }}
      />
    );
  }

  const seoProps = getPropsByRoute(newRoute, t, getHreflangHref);

  // Routes getPropsByRoute has no case for (developer page, the Discover
  // tabs, ...) fall back to an empty title/description/href. Rendering
  // SEOPage for them emits an empty <title> ahead of any real one plus a
  // full hreflang set with href="". Those pages either render their own
  // SEO tags (DeveloperPageSeo) or have none yet, so emit nothing here.
  if (!seoProps.title) {
    return null;
  }

  return <SEOPage metaTagsAggregator={metaTagsAggregator} {...seoProps} />;
}
